import { useEffect, useState } from 'react';

export const DEFAULT_PAGE_SIZE = 15;
export const DEFAULT_CURRENT_PAGE = 1;
export const MAX_PAGE_DISPLAY = 5;

export enum PaginationAction {
  SelectFirstPage,
  SelectPrevPage,
  SelectNextPage,
  SelectLastPage,
  SelectPage,
}

type UsePaginationProps = {
  itemCount: number;
  pageSize?: number;
  currentPage?: number;
  maxPageDisplay?: number;
};

type PaginationState = {
  totalPages: number;
  currentPage: number;
  startPage: number;
  endPage: number;
};

export function usePagination({
  itemCount,
  pageSize = DEFAULT_PAGE_SIZE,
  currentPage = DEFAULT_CURRENT_PAGE,
  maxPageDisplay = MAX_PAGE_DISPLAY,
}: UsePaginationProps): [
  PaginationState,
  (action: PaginationAction, pageNum?: number) => number,
  (action: PaginationAction) => boolean,
] {
  // Calculate pagination values
  const calculatePaginationState = (
    itemCount: number,
    pageSize: number,
    currentPage: number,
    maxPageDisplay: number,
  ): PaginationState => {
    const totalPages = Math.max(1, Math.ceil(itemCount / pageSize));

    // Ensure currentPage is within valid range
    const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages));

    let startPage: number;
    // Calculate startPage based on centering current page
    startPage = Math.max(1, validCurrentPage - Math.floor(maxPageDisplay / 2));
    const endPage = Math.min(totalPages, startPage + maxPageDisplay - 1);

    // If endPage is capped by totalPages, adjust startPage
    if (endPage === totalPages && endPage - startPage < maxPageDisplay - 1) {
      startPage = Math.max(1, endPage - maxPageDisplay + 1);
    }

    return {
      totalPages,
      currentPage: validCurrentPage,
      startPage,
      endPage,
    };
  };

  const [state, setState] = useState<PaginationState>(() =>
    calculatePaginationState(itemCount, pageSize, currentPage, maxPageDisplay),
  );

  useEffect(() => {
    setState(
      calculatePaginationState(
        itemCount,
        pageSize,
        currentPage,
        maxPageDisplay,
      ),
    );
  }, [itemCount, pageSize, currentPage, maxPageDisplay]);

  // Update pagination based on action
  const updatePagination = (
    action: PaginationAction,
    pageNum?: number,
  ): number => {
    const { totalPages, currentPage } = state;
    let newPage = currentPage;

    switch (action) {
      case PaginationAction.SelectFirstPage:
        newPage = 1;
        break;
      case PaginationAction.SelectPrevPage:
        newPage = Math.max(1, currentPage - 1);
        break;
      case PaginationAction.SelectNextPage:
        newPage = Math.min(totalPages, currentPage + 1);
        break;
      case PaginationAction.SelectLastPage:
        newPage = totalPages;
        break;
      case PaginationAction.SelectPage:
        // Ensure pageNum is within valid range
        if (pageNum === undefined) {
          newPage = currentPage;
        } else {
          newPage = Math.max(1, Math.min(pageNum, totalPages));
        }
        break;
    }

    const newState = calculatePaginationState(
      itemCount,
      pageSize,
      newPage,
      maxPageDisplay,
    );
    setState(newState);

    return newPage;
  };

  const canNavigate = (action: PaginationAction): boolean => {
    const { totalPages, currentPage } = state;

    switch (action) {
      case PaginationAction.SelectFirstPage:
      case PaginationAction.SelectPrevPage:
        return currentPage > 1;
      case PaginationAction.SelectNextPage:
      case PaginationAction.SelectLastPage:
        return currentPage < totalPages;
      case PaginationAction.SelectPage:
        return true;
      default:
        return true;
    }
  };

  return [state, updatePagination, canNavigate];
}
